import React, { useState, useCallback } from "react";
import Toolbar from "@material-ui/core/Toolbar";
import { Button, Dialog, DialogTitle, DialogContent, TextField as TextFieldMaterial, DialogActions } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles";
import DialogEditProductComponent from "./editProductPrice";
import DialogBatchEditProductComponent from "./batchEditProductPrice";

import { USER_GROUP_OWNER_AGENCY, USER_GROUP_ADMIN } from "../../utils/const";
import { Pagination, PriceField, ButtonField } from "../../partials";
import ExportButton from "../exportWarehouse";
import {
    Datagrid,
    TextField,
    TextInput,
    Filter,
    NumberField,
    ImageField,
    List,
    useRedirect,
    usePermissions,
    useDataProvider,
    CRUD_GET_LIST,
} from "react-admin";

import EditIcon from "@material-ui/icons/Edit";
import { useMutation, useNotify } from 'react-admin';

const useStyles = makeStyles((theme) => ({
    content: {
        overflow: "auto",
    },
}));

const FilterComponents = (props) => {
    return (
        <Filter {...props}>
            {/* Source is intentionally dot-free: react-admin's <Filter> form
                (react-final-form) treats a dotted source as a nested path
                (source="p.name_search" -> filterValues = {p: {name_search}}),
                not a literal flat key, so a "p.xxx" source here silently
                gets dropped by _convertFilter2String's nested-object filter.
                warehouseAgencyV2.js remaps this to the real "p.name_search"
                filter key. */}
            <TextInput source="warehouse_search" label="Tìm theo tên sản phẩm" />
        </Filter>
    );
};

const ActionComponents = ({
    basePath,
    currentSort,
    displayedFilters,
    exporter,
    filters,
    filterValues,
    resource,
    showFilter,
    total,
}) => {
    const redirect = useRedirect();
    const onClick = () => {
        redirect("/warehouseagenciestransfer");
    };
    const { permissions: auth } = usePermissions();

    return (
        <Toolbar>
            {filters &&
                React.cloneElement(filters, {
                    resource,
                    showFilter,
                    displayedFilters,
                    filterValues,
                    context: "button",
                })}
            {auth === USER_GROUP_OWNER_AGENCY ? (
                <Button color="primary" onClick={onClick}>
                    Planing
                </Button>
            ) : (
                ""
            )}
            <ExportButton
                disabled={total === 0}
                resource={resource}
                sort={currentSort}
                filter={filterValues}
                exporter={exporter}
            />
            <ImportProductPriceButton />
        </Toolbar>
    );
};

export const ImportProductPriceButton = (props) => {
    const [open, setOpen] = useState(false);
    const notify = useNotify();

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <Button
                variant="outlined"
                onClick={handleOpen}
                style={{ marginLeft: '10px' }}
            >
                Import Prices
            </Button>

            {open && (
                <DialogBatchEditProductComponent
                    anchor="right"
                    handleClose={handleClose}
                    title="Import Product Price"
                    maxWidth="md"
                    open={open}
                    fullWidth
                />
            )}
        </>
    );
}

export const SuppendButton = ({ onSuccessRefetch, ...props }) => {
    const notify = useNotify();
    const { record } = props;
    const id = (record && record.aw) ? record.aw.id : "";
    const status = (record && record.aw) ? record.aw.warehouse_status : "";

    const [handleSubmit, { isLoading }] = useMutation(
        {
            type: 'suppendProduct',
            resource: 'warehouseagenciesv2',
            payload: { agency_warehouse_id: id, is_suppend: status !== 'WAREHOUSE_STATUS_SUPPENDED' },
        },
        {
            undoable: false,
            onSuccess: ({ data, status, message }) => {
                if (status) {
                    // Refetch the current page's real data instead of refresh() (which
                    // would clear the whole list cache and force-remount the Datagrid,
                    // resetting scroll/page/filter/sort). This keeps list state intact
                    // while still guaranteeing accurate data (a suspend/publish action
                    // can flip the status of several rows sharing the same product,
                    // e.g. S/M/L variants).
                    if (onSuccessRefetch) onSuccessRefetch();
                    notify('Done', 'info', {}, false);
                } else {
                    notify(`Error: ${message}`, 'warning')
                }
            },
            onFailure: (error) => notify(`Error: ${error.message}`, 'warning'),
        }
    );


    if (status === 'WAREHOUSE_STATUS_SUPPENDED') {
        return (
            <>
                <Button variant="outlined" color="primary" disabled={isLoading} onClick={handleSubmit}>Publish</Button>
            </>
        )
    }
    return (
        <>
            <Button variant="outlined" color="secondary" disabled={isLoading} onClick={handleSubmit}>Suppend</Button>
        </>
    )
}

const EditProductButtonComponent = (props) => {
    const handleClick = () => {
        props.handleClick(props.record);
    };
    return (
        <ButtonField
            {...props}
            onClick={handleClick}
            name="Edit"
            addLabel={false}
            color="primary"
            bIcon={<EditIcon />}
        />
    );
};


export const DatagridComponent = ({ hasShow, hasEdit, hasList, ...props }) => {
    const classes = useStyles();
    const { permissions: auth } = usePermissions();
    const [isEditProduct, setIsEditProduct] = useState();
    const [product, setProduct] = useState({});
    const dataProvider = useDataProvider();
    const {
        resource,
        page,
        perPage,
        currentSort,
        filterValues,
        total,
        id: parentAgencyId,
        activeSearch,
    } = props;

    // This Datagrid is mounted in two different contexts, which inject
    // different props:
    // - Agency's own "Nhà kho" list (<List>): react-admin injects
    //   page/perPage/filterValues, so we can refetch the exact same
    //   page/filter/sort that's on screen.
    // - Admin's "Agency > Show > Warehouse" tab (<ReferenceManyField>):
    //   react-admin does NOT inject page/perPage/filter to the child (only
    //   resource/ids/data/currentSort/total) — so there is no "current page"
    //   to replay. Instead we refetch this agency's whole warehouse list in
    //   one page, filtered by "aw.agency_id" (the same filter key
    //   ReferenceManyField itself already uses in agencies/detail.js) PLUS
    //   the current search term (passed down explicitly as `activeSearch`,
    //   since ReferenceManyField doesn't expose its own filter to children)
    //   so the refetched dataset matches what's actually filtered on screen.
    // Either way this calls the SAME dataProvider.getList(), which updates
    // the SAME shared Redux record pool (state.admin.resources.<resource>.data)
    // that the already-rendered rows (keyed by id) read from — so both
    // contexts update their rows in place, with no page/filter/sort/scroll
    // reset and no second implementation of the refresh mechanism.
    const hasListPagination = typeof page !== "undefined" && typeof perPage !== "undefined";
    const refetchCurrentPage = useCallback(() => {
        dataProvider.getList(
            resource,
            {
                pagination: hasListPagination
                    ? { page, perPage }
                    : { page: 1, perPage: total || 1000 },
                sort: currentSort,
                filter: hasListPagination
                    ? filterValues
                    : {
                        "aw.agency_id": parentAgencyId,
                        ...(activeSearch ? { "p.name_search": activeSearch } : {}),
                      },
            },
            { action: CRUD_GET_LIST }
        );
    }, [
        dataProvider,
        resource,
        page,
        perPage,
        hasListPagination,
        currentSort,
        filterValues,
        activeSearch,
        total,
        parentAgencyId,
    ]);

    const handleCloseEditProduct = () => {
        setIsEditProduct(false);
    };
    const handleEditProduct = (data) => {
        setIsEditProduct(true);
        setProduct(data);
    };

    const rowStyle = (record, index) => {
        return {
            backgroundColor:
                (record && record.quantity <= 0)
                    ? "#dadada"
                    : (record && record.quantity < 10)
                        ? "#fffedb"
                        : "unset",
        }
    };
    return (
        <>
            {isEditProduct && (
                <DialogEditProductComponent
                    // {...props}
                    classesContent={{
                        root: classes.rootContent,
                    }}
                    id={props.id}
                    record={product}
                    product={product}
                    anchor="right"
                    handleClose={handleCloseEditProduct}
                    title="Edit Product"
                    maxWidth="md"
                    open
                    fullWidth
                />
            )}
            <Datagrid {...props} rowStyle={rowStyle}>
                <TextField source="p.name" label="Name" />
                <ImageField source="p.images[0]" label="Images" />
                <TextField source="p.description" label="Description" />
                <PriceField source="aw.unit_price" label="Unit Price" />
                <PriceField source="aw.agency_price" label="Agency Price" />
                <PriceField source="aw.shell_price" label="Shell Price" />
                <NumberField source="p.unit_point" label="Unit Point" />
                <NumberField source="p.net_weight" label="Net Weight" />
                <NumberField source="aw.quantity" label="Quantity" />
                {(auth === USER_GROUP_ADMIN || auth === USER_GROUP_OWNER_AGENCY) ? (
                    <SuppendButton
                        label="Status"
                        sortBy="aw.status"
                        sortByOrder="DESC"
                        onSuccessRefetch={refetchCurrentPage}
                    />
                ) : (
                    ""
                )}
                <EditProductButtonComponent handleClick={handleEditProduct} />
            </Datagrid>
        </>
    );
};
const ListComponent = (props) => {
    return (
        <List
            {...props}
            pagination={<Pagination />}
            perPage={20}
            title="Warehouse Statistics"
            bulkActionButtons={false}
            filters={<FilterComponents />}
            sort={{ field: "aw.created_at", order: "DESC" }}
            actions={<ActionComponents />}
        >
            <DatagridComponent {...props} />
        </List>
    );
};
export default ListComponent;
